// components/Cart.js
import React, { useState, useEffect } from 'react';
import useCartStore from '../store/cartStore';
import useUserStore from '../store/userStore';

function Cart() {
  const cartItems = useCartStore((state) => state.cartItems);
  const totalPrice = useCartStore((state) => state.totalPrice);
  const removeFromCart = useCartStore((state) => state.removeFromCart);
  const clearCart = useCartStore((state) => state.clearCart);
  const closeCart = useCartStore((state) => state.closeCart);
  const [fadingOut, setFadingOut] = useState([]);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);
  const { isLoggedIn, fetchWithTokenRefresh, refreshTokenIfNeeded } = useUserStore();
  const [removePending, setRemovePending] = useState(false);

  // Ensure we clear any stuck removal state when cart changes
  useEffect(() => {
    if (removePending && cartItems.length > 0) {
      setTimeout(() => setRemovePending(false), 1000);
    }
  }, [cartItems, removePending]);

  const handleRemove = async (assetId) => {
    // Prevent duplicate remove operations
    if (removePending || fadingOut.includes(assetId)) {
      return;
    }
    
    // Log the removal for debugging
    console.log("[CART] Removing item with assetId:", assetId);
    setRemovePending(true);
    
    // Start fading animation
    setFadingOut([...fadingOut, assetId]);
    
    try {
      // Call removeFromCart after a brief animation delay
      setTimeout(async () => {
        const success = await removeFromCart(assetId);
        
        if (!success) {
          console.error("[CART] Failed to remove item from cart:", assetId);
        }
        
        // Always clear the item from fading state
        setFadingOut((prev) => prev.filter((id) => id !== assetId));
        setRemovePending(false);
      }, 500);
    } catch (error) {
      console.error("[CART] Error in handleRemove:", error);
      setFadingOut((prev) => prev.filter((id) => id !== assetId));
      setRemovePending(false);
    }
  };

  const handleClearCart = (e) => {
    e.preventDefault();
    clearCart();
    closeCart();
  };

  const handleCheckout = async () => {
    setCheckoutLoading(true);
    setCheckoutError(null);
    
    try {
      // Получаем текущий URL для возврата
      const currentUrl = window.location.href;

      // Проверяем, авторизован ли пользователь
      if (!isLoggedIn) {
        console.log("[CART] User not logged in, redirecting to login");
        // Перенаправляем на страницу логина с параметром next
        window.location.href = `/login?next=${encodeURIComponent(currentUrl)}`;
        setCheckoutLoading(false);
        return;
      }

      // Убедимся, что у нас действительный токен перед запросом
      try {
        await refreshTokenIfNeeded();
      } catch (tokenError) {
        console.error("[CART] Failed to refresh token:", tokenError);
        setCheckoutError("Authentication error. Please try logging in again.");
        setCheckoutLoading(false);
        return;
      }

      console.log("[CART] Starting checkout process...");
      
      // Отправляем запрос на чекаут с использованием fetchWithTokenRefresh
      const response = await fetchWithTokenRefresh(
        `${process.env.NEXT_PUBLIC_BACKEND_URL}/cart/checkout`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ return_url: currentUrl }),
          requireAuth: true // Важный флаг, указывающий что нужно использовать JWT
        }
      );

      if (!response.ok) {
        console.error("[CART] Checkout API error:", response.status);
        
        let errorMessage = "An error occurred during checkout";
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
          console.error("[CART] Server error details:", errorData);
        } catch (e) {
          // Если не удалось распарсить JSON с ошибкой
          console.error("[CART] Failed to parse error response");
        }
        
        // Особая обработка ошибок аутентификации
        if (response.status === 401) {
          console.error("[CART] Authentication failed during checkout");
          errorMessage = "Authentication error. Please try logging in again.";
          
          // Можно добавить автоматический переход на страницу логина
          setTimeout(() => {
            window.location.href = `/login?next=${encodeURIComponent(currentUrl)}`;
          }, 2000);
        }
        
        setCheckoutError(errorMessage);
        setCheckoutLoading(false);
        return;
      }

      // Получаем URL для чекаута и перенаправляем
      const data = await response.json();
      console.log("[CART] Checkout successful, redirecting to Stripe");
      window.location.href = data.checkout_url;
    } catch (error) {
      console.error('[CART] Unexpected checkout error:', error);
      setCheckoutError("An error occurred during checkout. Please try again.");
      setCheckoutLoading(false);
    }
  };

  return (
    <div className="max-w-7xl w-[90%] mx-auto overflow-hidden">
      {cartItems.length === 0 ? (
        <div className="flex justify-center items-center p-8 text-center">
          <p className="text-lg">Your cart is empty.</p>
        </div>
      ) : (
        <div className="grid grid-cols-6 gap-4">
          <div className="col-span-4 sm:col-span-4 md:col-span-4 lg:col-span-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 overflow-y-auto max-h-[380px] pr-2.5 pb-5 w-full">
              {cartItems.map((item) => (
                <div 
                  className={`relative rounded-lg border border-black/10 h-[210px] flex justify-center items-center mt-0.5 w-full ${
                    fadingOut.includes(item.assetId) ? 'opacity-0 transform -translate-y-5 transition-all duration-500' : ''
                  }`} 
                  key={item.assetId || item.id}
                >
                  {item.mediaType === 'icon' && item.slug && item.collection && (
                    <div className="flex justify-center items-center h-full">
                      <img
                        src={`https://icons-pbl.davooda.com/${item.collection}/png-256px/${item.slug}-256x256.png`}
                        alt={item.alt || 'Icon'}
                        className="w-16 h-16"
                      />
                    </div>
                  )}
                  {item.mediaType === 'iconset' && item.slug && (
                    <div className="flex justify-center items-center">
                      <img
                        src={`https://icons-pbl.davooda.com/outline/set-preview/${item.slug}.png`}
                        alt={item.alt || 'Icon Set'}
                        className="w-[164px] h-auto mt-5"
                      />
                    </div>
                  )}
                  <div className="absolute top-1.5 right-2.5 text-xs">
                    ${item.product && typeof item.product.price === 'number' ? item.product.price.toFixed(2) : '0.00'}{' '}
                    <button
                      className="text-black underline ml-1 cursor-pointer"
                      disabled={fadingOut.includes(item.assetId) || removePending}
                      onClick={(e) => {
                        e.preventDefault();
                        handleRemove(item.assetId);
                      }}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="col-span-2 pl-0 md:pl-2.5 flex flex-col items-start">
            <div className="text-2xl text-black mb-5">
              Total: ${typeof totalPrice === 'number' ? totalPrice.toFixed(2) : '0.00'}
            </div>
            
            {checkoutError && (
              <div className="w-full bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                <p>{checkoutError}</p>
              </div>
            )}
            
            <button
              className="w-full bg-black text-white font-bold py-3.5 px-2.5 rounded transition-all duration-300 text-base mt-4"
              title="Proceed to checkout"
              onClick={handleCheckout}
              disabled={checkoutLoading}
            >
              Proceed to checkout
              {checkoutLoading && (
                <span className="ml-4 inline-block align-middle">
                  <svg 
                    width="20" 
                    height="20" 
                    viewBox="0 0 100 100" 
                    preserveAspectRatio="xMidYMid" 
                    className="align-middle"
                  >
                    <circle cx="50" cy="50" fill="none" stroke="#fff" strokeWidth="8" r="35" strokeDasharray="164.93361431346415 56.97787143782138">
                      <animateTransform 
                        attributeName="transform" 
                        type="rotate" 
                        repeatCount="indefinite" 
                        dur="1s" 
                        values="0 50 50;360 50 50" 
                        keyTimes="0;1"
                      />
                    </circle>
                  </svg>
                </span>
              )}
            </button>
            <div className="mt-10">
              <button onClick={handleClearCart} className="text-black underline">
                Clear Cart
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Cart;